<template>
  <div data-testid="wealth-performance-accordion">
    <div
      v-for="(history, historyIndex) in historyOverview"
      :key="historyIndex"
      :class="[
        'wealth-performance-accordion',
        {
          'wealth-performance-accordion__opened': currentIndex === historyIndex
        }
      ]"
    >
      <Accordion
        class="wealth-performance-accordion__item"
        :open="currentIndex === historyIndex"
        @accordion-changed="
          open => openPeriodHistory(historyIndex, open, history.label)
        "
      >
        <template #title>
          <Row :has-line="false" class="wealth-performance-accordion__row">
            <template #top-left>{{ history.label }}</template>
            <template #top-right>
              <Money>{{ history.value }}</Money>
            </template>
            <template #bottom-right>
              <Money>{{ history.valueDescription }}</Money>
            </template>
          </Row>
        </template>
        <section class="wealth-performance-accordion__content">
          <SpinnerLoader v-if="isLoading" />
          <HelperViewStatus
            v-else-if="hasError"
            title="Erro de carregamento"
            description="Não foi possível carregar o conteúdo. Por favor, tente novamente."
            button-label="Tentar novamente"
            status="alert"
            @click="getPerformanceDetails(history.label)"
          />
          <PerformanceDetails
            v-else-if="summaryDetails"
            :summary-details="summaryDetails"
            :updated-at="updatedAt"
          />
        </section>
      </Accordion>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';

import {
  Row,
  SpinnerLoader,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { Accordion } from '@/modules/common/components/Accordion';
import { Money } from '@/modules/common/components/Money';
import { PerformanceDetails } from '@/modules/common/components/PerformanceDetails';

import {
  getPortfolioPerformanceDetails,
  getWealthPerformanceDetails,
  IDefaultProps,
  IPerformanceDetailResponse
} from '@/modules/wealth/services/investment';

interface Props {
  historyOverview: IDefaultProps[];
  updatedAt: string;
  portfolioId: string;
}

const { portfolioId } = defineProps<Props>();

const currentIndex = ref(-1);
const isLoading = ref(true);
const hasError = ref(false);
const summaryDetails = ref<IPerformanceDetailResponse | null>(null);

async function openPeriodHistory(index: number, isOpen: boolean, year: string) {
  if (isOpen) {
    currentIndex.value = index;
    await getPerformanceDetails(year);
  } else {
    currentIndex.value = -1;
  }
}

async function getPerformanceDetails(year: string) {
  if (portfolioId) {
    await fetchPortfolioPerformanceDetails(year);
    return;
  }
  await fetchWealthPerformanceDetails(year);
}

async function fetchPortfolioPerformanceDetails(year: string) {
  try {
    hasError.value = false;
    isLoading.value = true;

    summaryDetails.value = await getPortfolioPerformanceDetails(
      Number(year),
      portfolioId
    );
  } catch (error) {
    console.error(error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchWealthPerformanceDetails(year: string) {
  try {
    hasError.value = false;
    isLoading.value = true;

    summaryDetails.value = await getWealthPerformanceDetails(year);
  } catch (error) {
    console.error(error);
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}
</script>

<style lang="less" scoped>
.wealth-performance-accordion {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 @size-spacing-x400;

  &__opened {
    background: @background-primary;
  }

  &__item {
    width: 100%;
    max-width: 1058px;
  }

  &__row {
    background: transparent;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;

    .helper-view {
      max-width: 320px;
    }
  }
}

::v-deep .wrn-accordion .wrn-accordion-text {
  margin-bottom: 0;
}
</style>
